import {
  useRef,
  useState,
  useEffect,
  useCallback,
  useLayoutEffect
} from 'react';
import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { useMediaQuery, useTheme } from '@material-ui/core';
import {
  CapaErrorCode,
  CapaPaymentMethodType,
  CreatePurchaseOrderInput,
  useFetchMyRefundBankAccountsLazyQuery
} from '@generated/graphql';
import usePayment from './usePayment';
import useDocuments from '@hooks/useDocuments';
import useFetchPurchaseOrderBaseData from './useFetchData';
import useCreatePurchaseOrder from './useCreatePurchaseOrder';
import useRegistRefundAccount from './useResgistRefundAccount';
import { PaymentInput } from '../Payment/Method';
import { getPriceDatas } from '../utils/getTotalAmount';
import { Props } from '../CreatePurchaseOrderContainer';
import { convertTypeToCreatePurchaseOrderInput } from '../utils/processInput';
import { CLOSED_CHAT_ROOM_STATES } from '../types';
import { storage } from '@utils/storage';
import useQueryString from '@hooks/useQueryString';
import sendPaymentWebhook from '@utils/paymentWebhook';
import AppConfig from '@config';

type CreatePurchaseOrderForm = CreatePurchaseOrderInput & PaymentInput;

const isProductionEnv = AppConfig.mode === 'production';

const usePurchaseOrderForm = ({
  rfqId = '',
  partnerId = '',
  chatRoomId = '',
  chatRoomStatus
}: Props) => {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('md'));
  const isDashboard = window.location.pathname.includes('/dashboard');
  const isClosed = CLOSED_CHAT_ROOM_STATES.includes(chatRoomStatus);
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const { onInitDocuments } = useDocuments();
  const [submitOpen, setSubmitOpen] = useState(false);
  const [isPaymentModalOn, setIsPaymentModalOn] = useState<boolean>(false);
  const isPaymentProcessing = useRef<boolean>(false);
  const createPurchaseOrderInput = useRef<CreatePurchaseOrderInput | null>(
    null
  );
  const form = useForm<CreatePurchaseOrderForm>({
    shouldUnregister: true
  });
  const registRefundAccount = useRegistRefundAccount();
  const {
    baseData,
    loading,
    rfqDetail,
    refundAccount,
    partner
  } = useFetchPurchaseOrderBaseData({ rfqId, partnerId });

  const { onReplaceQueryString, query } = useQueryString();

  const { onCreatePurchaseOrder } = useCreatePurchaseOrder({
    rfqId,
    partnerId,
    chatRoomId
  });
  const { onPay, onCreatePayment, onRedirectAfterPaymentMobile } = usePayment({
    rfqId,
    partnerId
  });
  const [fetchRefundAccounts] = useFetchMyRefundBankAccountsLazyQuery({
    fetchPolicy: 'no-cache'
  });
  const { totalAmount } = getPriceDatas(baseData);
  const { projectName } = rfqDetail ?? {};

  const handleRefundAccount = useCallback(async () => {
    const { bank, bankAccount, pay_method } = form.getValues();
    const isVbank = pay_method === CapaPaymentMethodType.VirtualAccount;

    if (refundAccount.current || !isVbank) return;

    if (!refundAccount.current && (!bank || !bankAccount)) {
      throw new Error('환불계좌 정보를 입력해주세요.');
    }

    if (!bank) {
      throw new Error('은행정보가 정확하지 않습니다.');
    }

    await registRefundAccount({
      account: bankAccount ?? '',
      bankCode: bank,
      onValidateError: () => {
        form.setError('bank', {
          message: '계좌정보가 정확하지 않습니다.'
        });
      }
    });

    const { data: fetchRefundAccountResponse } = await fetchRefundAccounts();
    const refundAccountList =
      fetchRefundAccountResponse?.fetchMyRefundBankAccounts?.data;
    const latestRefundAccount = refundAccountList?.[0];

    if (!latestRefundAccount) {
      throw new Error('환불계좌를 불러오는데 실패했습니다.');
    }

    refundAccount.current = latestRefundAccount;
    storage.setItem.TEMP_REFUND_ACCOUNT_ID(latestRefundAccount?.id);
  }, [form, refundAccount, registRefundAccount, fetchRefundAccounts]);

  const onValidateForm = useCallback(
    (callback: () => void) =>
      form.handleSubmit(
        async (data) => {
          createPurchaseOrderInput.current = convertTypeToCreatePurchaseOrderInput(
            {
              data,
              basedData: baseData
            }
          );

          onReplaceQueryString({
            ...(query as any),
            p: '3'
          });
          callback();
        },
        (e) => {
          enqueueSnackbar('작성내용을 확인해주세요.');
        }
      ),
    [enqueueSnackbar, form, baseData, onReplaceQueryString, query]
  );

  const successRedirect = useCallback(
    async (chatRoomId?: string) => {
      const { pathname } = history.location;

      if (isDashboard && !chatRoomId) {
        return history.push(pathname);
      }

      isPaymentProcessing.current = true;
      setIsPaymentModalOn(true);

      const nextRoute = isDashboard
        ? `/chat/${chatRoomId}?p=com`
        : `${pathname}?p=com`;

      history.push(nextRoute);
      enqueueSnackbar('발주서를 발행했습니다.', {
        variant: 'success'
      });
    },
    //eslint-disable-next-line
    []
  );

  const onSubmit = useCallback(
    async (closeModal: () => void) => {
      if (!projectName) {
        throw new Error('견적 요청 데이터를 불러오는데 실패했습니다.');
      }

      isPaymentProcessing.current = true;
      setIsPaymentModalOn(true);

      try {
        await handleRefundAccount();

        const input = createPurchaseOrderInput.current;
        const paymentMethod = form.getValues('pay_method');

        if (!input) {
          throw new Error('작성된 발주서 데이터를 찾지못했습니다.');
        }

        if (
          paymentMethod === CapaPaymentMethodType.VirtualAccount &&
          !refundAccount?.current
        ) {
          throw new Error(
            '등록된 환불계좌를 찾을 수 없습니다. 환불계좌를 확인해주세요.'
          );
        }

        const purchaseOrderId = await onCreatePurchaseOrder(input);

        closeModal();

        onReplaceQueryString({
          ...(query as any),
          p: '4'
        });
        const paymentResult = await onPay({
          paymentMethod,
          merchant_uid: purchaseOrderId,
          projectName,
          totalAmount
        });

        await onCreatePayment({
          paymentResult,
          purchaseOrderId,
          chatRoomStatus
        });

        if (isProductionEnv) {
          sendPaymentWebhook({
            amount: totalAmount,
            method: paymentMethod,
            projectName: projectName
          });
        }

        successRedirect(chatRoomId);
      } catch (e) {
        enqueueSnackbar((e as Error)?.message, {
          variant: 'error'
        });
      } finally {
        isPaymentProcessing.current = false;
        setIsPaymentModalOn(false);
      }
    },
    [
      projectName,
      handleRefundAccount,
      form,
      refundAccount,
      onCreatePurchaseOrder,
      onReplaceQueryString,
      query,
      onPay,
      totalAmount,
      onCreatePayment,
      chatRoomStatus,
      successRedirect,
      chatRoomId,
      enqueueSnackbar
    ]
  );

  useLayoutEffect(() => {
    // 모바일 결제 후 리다이렉드 퇸 경우
    isPaymentProcessing.current = true;
    setIsPaymentModalOn(true);

    if (!refundAccount.current)
      onRedirectAfterPaymentMobile({
        chatRoomStatus,
        isPaymentProcessing
      })
        .then((res) => {
          if (!res) return;
          const { result, chatRoomId: tempChatRoomId } = res;
          const isSuccess = result === CapaErrorCode.Success;
          if (isSuccess) {
            successRedirect(tempChatRoomId || chatRoomId);
          }
        })
        .finally(() => {
          isPaymentProcessing.current = false;
          setIsPaymentModalOn(false);
        });

    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    // 스크롤 상단 이동
    const container = document.getElementById('drawer-content-purchaseorder');
    container?.scrollTo({ top: 0 });
  }, []);

  useEffect(
    // Clean up
    () => () => {
      onInitDocuments();
    },
    [onInitDocuments]
  );

  return {
    form,
    matches,
    loading,
    partner,
    baseData,
    isClosed,
    rfqDetail,
    submitOpen,
    isDashboard,
    isPaymentModalOn,
    isPaymentProcessing,
    onSubmit,
    setSubmitOpen,
    onValidateForm
  };
};

export default usePurchaseOrderForm;
